import { useMemo, type SVGProps } from "react";
import styled from "styled-components";

import type { TrackingPreviewContour } from "./types";

type AnnotationContourPresentation = "annotation" | "tracking";

const contourFillOpacity: Record<AnnotationContourPresentation, number> = {
  annotation: 0.3,
  tracking: 0.75,
};

const ContourLayer = styled.svg<{ $presentation: AnnotationContourPresentation }>`
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;
  color: var(--c6-annotation-target-color, currentColor);
  pointer-events: none;

  polygon {
    fill: currentColor;
    fill-opacity: ${({ $presentation }) => contourFillOpacity[$presentation]};
    stroke: currentColor;
    stroke-width: 0.004;
    stroke-linejoin: round;
  }
`;

function clampContourCoordinate(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

export function AnnotationContourLayer({
  contours,
  presentation,
  ...svgProps
}: Readonly<
  {
    contours: readonly TrackingPreviewContour[];
    presentation: AnnotationContourPresentation;
  } & Omit<SVGProps<SVGSVGElement>, "children">
>) {
  // contour 좌표(수백 점)의 문자열 조립은 contours identity에 캐시한다 — 부모는 시간/스냅샷
  // 갱신으로 자주 리렌더하지만 contour 자체는 프레임 단위로만 바뀐다.
  const contourPoints = useMemo(
    () =>
      contours.map((contour) =>
        contour
          .map((point) => `${clampContourCoordinate(point.x)},${clampContourCoordinate(point.y)}`)
          .join(" "),
      ),
    [contours],
  );
  if (contours.length === 0) return null;

  return (
    <ContourLayer
      $presentation={presentation}
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      aria-hidden="true"
      {...svgProps}
    >
      {contourPoints.map((points, contourIndex) =>
        points ? <polygon key={contourIndex} points={points} /> : null,
      )}
    </ContourLayer>
  );
}
